import {
    getGetBatteryUrl,
    getUpdateBatteryUrl
} from 'generatedTypes/asset/services/battery-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { BATTERIES_API } from './constants';
import type { BatteryResponse, CreateBattery, FetchBattery, UpdateBattery } from './types';

const useBattery = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry, withRetryAndDuplicateCheck } = useRetry();
    const { BATTERY_NAME_DUPLICATE_ERROR, CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchBattery: FetchBattery = async ({ id }) => {
        try {
            setIsLoading(true);
            const url = getGetBatteryUrl(id);

            return await withRetry(
                () => authenticatedGet<BatteryResponse>(url),
                CONNECTION_ERROR,
                () => fetchBattery({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const createBattery: CreateBattery = async ({ battery }) => {
        try {
            setIsLoading(true);

            return await withRetryAndDuplicateCheck(
                () => authenticatedPost<BatteryResponse>(BATTERIES_API, JSON.stringify(battery)),
                SAVE_FAILED,
                BATTERY_NAME_DUPLICATE_ERROR,
                () => createBattery({ battery })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateBattery: UpdateBattery = async ({ battery, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateBatteryUrl(id);

            return await withRetry(
                () => authenticatedPut<BatteryResponse>(url, JSON.stringify(battery)),
                SAVE_FAILED,
                () => updateBattery({ battery, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createBattery,
        fetchBattery,
        isLoading,
        updateBattery
    };
};

export default useBattery;
